import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import CommonRadioCheckboxFilter from 'src/common/refactoredComponents/filter/CommonRadioCheckboxFilter';
import SearchBox from 'src/common/refactoredComponents/searchbox/index';
import SortingButtonPanel from 'src/common/refactoredComponents/sortingButtonPanel';
import { Alphabets } from 'src/constants/productGuideConstants';
import { scrollBarStyle } from 'src/styles/CommonStyles.styled';
import { Dot } from 'src/styles/CommonStyles.styled';

// Images
import DownArrowFilter from 'src/assets/images/DownArrowFilter.svg';
import AZButtonBlue from 'src/assets/images/AZBlueButton.svg';
import AZButtonGrey from 'src/assets/images/AZGreyButton.svg';
import SearchBold from 'src/assets/images/SearchBold.svg';
import Search from 'src/assets/images/Search.svg';

// Styled Components
const FilterComponentWrapper = styled.div`
    background: #ffff;
    margin-bottom: 10px;
    margin-left: 16px;
    margin-right: 16px;
    box-shadow: 0px 0px 6px #00000029;
    border-radius: 6px;
`;

const FilterComponentHeaderFooter = styled.div`
    background: #ffff;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 19px;
    padding-right: 19px;
    padding-top: 18px;
    padding-bottom: 20px;
    border-radius: 6px;
    align-items: center;
    cursor: pointer;
`;

const SubContainerRows = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${(props) => props.padding || '17px 18px 17px 28px'};
    justify-content: space-between;
    cursor: ${(props) => props.cursor || 'default'};
`;

const TextBold = styled.div`
    text-align: left;
    font-family: 'AvenirNextWorld Demi';
    color: #140f4b;
    font-size: 15px;
    margin: 0;
    padding: 0;
    display: flex;
    align-self: center;
`;

const CardTitle = styled.div`
    font-family: 'AvenirNextWorld Demi';
    color: #140f4b;
    font-size: ${(props) => props.fontSize || '20px'};
`;

const ActionContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
`;

const SearchButtonIcon = styled.div`
    margin-left: 16px;
    cursor: pointer;
`;

const SortButtonIcon = styled.div`
    height: 30px;
    margin-left: 20px;
    cursor: pointer;
`;

const SearchBoldStyle = styled.div`
    width: 30px;
    height: 30px;
    border: 1px solid #1021eb;
    border-radius: 5px;
    padding: 4px;
    margin: 3px;
    box-shadow: 0px 0px 2px #00000029;
`;

const SearchStyle = styled.div`
    width: 30px;
    height: 30px;
    border: 1px solid #0000001a;
    border-radius: 5px;
    padding: 4px;
    margin: 3px;
    box-shadow: 0px 0px 2px #a09d9d;
`;

const ImageContainer = styled.img`
    transform: ${({ isExpanded }) => (isExpanded ? 'rotate(180deg)' : '')};
`;

const DownArrowContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

const SearchingItems = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 300px;
    -webkit-overflow-scrolling: touch;
    ${scrollBarStyle}
`;

const ProductCategoriesContainer = styled.div`
    margin: 0px;
    &:last-child {
        margin-bottom: 10px;
    }
`;

const LineSeparator = styled.hr`
    border: 0;
    border-top: 1px solid #eee;
    margin: 0;
`;

const PanelButtonGroup = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 16%);
    grid-template-rows: auto;
    column-gap: 2px;
    row-gap: 2px;
    margin: 10px 18px;
`;

const BrandFilter = ({
    header,
    filters = [],
    onCheckBoxChange,
    isExpanded,
    updateExpandedViews,
    view,
    checkBoxName,
    codeKey = 'filterCode',
    nameKey = 'filterName',
    persistSearchOnToggle = false,
}) => {
    const { t } = useTranslation();
    const [isSearch, setIsSearch] = useState(true);
    const [isSort, setIsSort] = useState(false);
    const [search, setSearch] = useState('');
    const [clickedIndex, setClickedIndex] = useState('');

    // Group brands alphabetically
    const groups = useMemo(() => {
        if (!filters || filters.length === 0) return {};
        return filters.reduce((acc, brand) => {
            const letter = brand[nameKey]?.[0]?.toUpperCase() || '#';
            acc[letter] = acc[letter] || [];
            acc[letter].push(brand);
            return acc;
        }, {});
    }, [filters, nameKey]);

    // Check if any brand is selected
    const hasSelectedBrands = useMemo(() => {
        return filters?.some((item) => item.isChecked) && filters.length > 1;
    }, [filters]);

    // Get disabled letters (letters with no brands)
    const getIsDisableLetters = useCallback(
        (letter) => {
            return !groups[letter] || groups[letter].length === 0;
        },
        [groups]
    );

    // Toggle between search and sort mode
    const searchOrSort = (isSearchMode) => {
        if (isSearchMode) {
            setIsSearch(true);
            setIsSort(false);
            setSearch('');
        } else {
            setIsSearch(false);
            setIsSort(true);
        }
    };

    // Update search text
    const updateSearch = (value) => {
        setSearch(value);
    };

    // Reset to search mode with the search bar visible
    const resetSearchAndSort = () => {
        setIsSearch(true);
        setIsSort(false);
        setSearch('');
        setClickedIndex('');
    };

    // Apply sort filter (scroll to letter)
    const applySortFilter = (label, index) => {
        setClickedIndex(index);
        const element = document.getElementById(label);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Toggle card expand/collapse
    const toggleCard = () => {
        if (!isExpanded && !persistSearchOnToggle) {
            resetSearchAndSort();
        }
        updateExpandedViews({ view, value: !isExpanded });
    };

    return (
        <FilterComponentWrapper>
            <FilterComponentHeaderFooter onClick={toggleCard}>
                <ActionContainer>
                    <TextBold>
                        {header}
                    </TextBold>
                    {isExpanded && (
                        <ButtonContainer>
                            <SearchButtonIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    searchOrSort(true);
                                    setClickedIndex('');
                                }}
                            >
                                {isSearch ? (
                                    <SearchBoldStyle>
                                        <img src={SearchBold} height="18px" alt="search" />
                                    </SearchBoldStyle>
                                ) : (
                                    <SearchStyle>
                                        <img src={Search} height="18px" alt="search" />
                                    </SearchStyle>
                                )}
                            </SearchButtonIcon>
                            <SortButtonIcon onClick={(e) => {
                                e.stopPropagation();
                                searchOrSort(false);
                            }}>
                                {isSort ? (
                                    <img src={AZButtonBlue} height="30px" width="30px" alt="sort" />
                                ) : (
                                    <img src={AZButtonGrey} height="30px" width="30px" alt="sort" />
                                )}
                            </SortButtonIcon>
                        </ButtonContainer>
                    )}
                </ActionContainer>
                <DownArrowContainer>
                    {hasSelectedBrands && !isExpanded && <Dot />}
                    <ImageContainer
                        src={DownArrowFilter}
                        alt="down-arrow"
                        isExpanded={isExpanded}
                    />
                </DownArrowContainer>
            </FilterComponentHeaderFooter>

            {isExpanded && (
                <>
                    <LineSeparator />
                    
                    {/* Search Mode */}
                    {isSearch && (
                        <>
                            <SearchBox
                                id="brand-filter-searchbox"
                                updateSearch={updateSearch}
                                searchProduct={search}
                                width="100%"
                                containerMobileWidth="calc(100% - 32px)"
                                borderRadius="20px"
                                marginDesktop="8px 8px"
                                paddingLeft="1rem"
                                isSearchIcon={false}
                                fontFamily="AvenirNextWorld Regular"
                                placeHolderFontFamily="AvenirNextWorld Regular"
                                fontSize="14px"
                                placeholder={t('scheduleSurgery.productGuide.searchBrandName')}
                                enterkeyhint="go"
                            />
                            <SearchingItems>
                                {groups &&
                                    Object.entries(groups)
                                        .filter(([, list]) => {
                                            return list.some((el) =>
                                                el[nameKey]
                                                    ?.toLowerCase()
                                                    .includes(search.toLowerCase())
                                            );
                                        })
                                        .sort()
                                        .map(([letterKey, list]) => {
                                            const filteredList = list.filter((el) =>
                                                el[nameKey]
                                                    ?.toLowerCase()
                                                    .includes(search.toLowerCase())
                                            );
                                            return (
                                                <div key={letterKey}>
                                                    <SubContainerRows padding="17px 18px 17px 28px">
                                                        <CardTitle fontSize="20px">
                                                            {letterKey}
                                                        </CardTitle>
                                                    </SubContainerRows>
                                                    <div>
                                                        {filteredList.map((item) => (
                                                            <ProductCategoriesContainer
                                                                key={item[codeKey]}
                                                            >
                                                                <SubContainerRows padding="8px 18px 8px 12px">
                                                                    <CommonRadioCheckboxFilter
                                                                        isCheckbox={true}
                                                                        labelWidth="100%"
                                                                        fontFamilyStyle="AvenirNextWorld Regular"
                                                                        leftPadding="15px"
                                                                        isChecked={item.isChecked}
                                                                        id={`brand-filter-${item[codeKey]}`}
                                                                        checkBoxName={checkBoxName}
                                                                        filterItem={item[nameKey]}
                                                                        filterItemId={item[codeKey]}
                                                                        onCheckBoxChange={(e) =>
                                                                            onCheckBoxChange(e, checkBoxName)
                                                                        }
                                                                    />
                                                                </SubContainerRows>
                                                            </ProductCategoriesContainer>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                            </SearchingItems>
                        </>
                    )}

                    {/* Sort Mode */}
                    {isSort && Alphabets && (
                        <>
                            <PanelButtonGroup>
                                {Alphabets().map((label, index) => (
                                    <SortingButtonPanel
                                        key={index}
                                        id={`brand-filter-sorting-${label}`}
                                        label={label}
                                        margin="0px"
                                        fontSize="20px"
                                        fontFamily={
                                            index === clickedIndex
                                                ? 'AvenirNextWorld Demi'
                                                : 'AvenirNextWorld Regular'
                                        }
                                        backgroundColor={
                                            index === clickedIndex ? '#FFEBBF' : ''
                                        }
                                        disabled={getIsDisableLetters(label)}
                                        border={
                                            index === clickedIndex
                                                ? '1px solid #F4AF3D'
                                                : ''
                                        }
                                        clicked={() => applySortFilter(label, index)}
                                    />
                                ))}
                            </PanelButtonGroup>
                            <SearchingItems id="brand-scrolling-div">
                                {groups &&
                                    Object.entries(groups)
                                        .sort()
                                        .map(([letterKey, list]) => (
                                            <React.Fragment key={letterKey}>
                                                <SubContainerRows
                                                    padding="17px 18px 17px 17px"
                                                    id={letterKey}
                                                >
                                                    <CardTitle fontSize="20px">
                                                        {letterKey}
                                                    </CardTitle>
                                                </SubContainerRows>
                                                <div>
                                                    {list.map((item) => (
                                                        <ProductCategoriesContainer
                                                            key={item[codeKey]}
                                                        >
                                                            <LineSeparator />
                                                            <SubContainerRows padding="17px 18px 17px 0px">
                                                                <CommonRadioCheckboxFilter
                                                                    isCheckbox={true}
                                                                    labelWidth="100%"
                                                                    fontFamilyStyle="AvenirNextWorld Regular"
                                                                    leftPadding="15px"
                                                                    isChecked={item.isChecked}
                                                                    checkBoxName={checkBoxName}
                                                                    filterItemId={item[codeKey]}
                                                                    filterItem={item[nameKey]}
                                                                    id={`brand-filter-sort-${item[codeKey]}`}
                                                                    onCheckBoxChange={(e) =>
                                                                        onCheckBoxChange(e, checkBoxName)
                                                                    }
                                                                />
                                                            </SubContainerRows>
                                                        </ProductCategoriesContainer>
                                                    ))}
                                                </div>
                                            </React.Fragment>
                                        ))}
                            </SearchingItems>
                        </>
                    )}

                    {/* Default Mode - No search or sort active */}
                    {!isSearch && !isSort && (
                        <SearchingItems>
                            {groups &&
                                Object.entries(groups)
                                    .sort()
                                    .map(([letterKey, list]) => (
                                        <div key={letterKey}>
                                            <SubContainerRows padding="17px 18px 17px 28px">
                                                <CardTitle fontSize="20px">
                                                    {letterKey}
                                                </CardTitle>
                                            </SubContainerRows>
                                            <div>
                                                {list.map((item) => (
                                                    <ProductCategoriesContainer
                                                        key={item[codeKey]}
                                                    >
                                                        <SubContainerRows padding="8px 18px 8px 12px">
                                                            <CommonRadioCheckboxFilter
                                                                isCheckbox={true}
                                                                labelWidth="100%"
                                                                fontFamilyStyle="AvenirNextWorld Regular"
                                                                leftPadding="15px"
                                                                isChecked={item.isChecked}
                                                                id={`brand-filter-default-${item[codeKey]}`}
                                                                checkBoxName={checkBoxName}
                                                                filterItem={item[nameKey]}
                                                                filterItemId={item[codeKey]}
                                                                onCheckBoxChange={(e) =>
                                                                    onCheckBoxChange(e, checkBoxName)
                                                                }
                                                            />
                                                        </SubContainerRows>
                                                    </ProductCategoriesContainer>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                        </SearchingItems>
                    )}
                </>
            )}
        </FilterComponentWrapper>
    );
};

export default BrandFilter;
